import { useEffect, useRef, useState } from 'react';
import { facilitySearch, type FacilitySearchRow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { pre, sx } from './util';

/** The design's page: at most 8 rows, "All facilities" included. The list never renders in full. */
const SHOWN = 8;
const WAIT_MS = 250;
/** PROPOSED on GET /facilities/search: the building's enrolled residents, for the row's meta. */
type Row = FacilitySearchRow & { residents?: number | null };

/**
 * "Change facility" (design pv.facToggle / facHits / facFoot, T12356-12372): a search field,
 * "All facilities" with the network's building count, then buildings from the server's own
 * search (GET /facilities/search, scoped by the server; empty text lists the first page).
 * Typing narrows it on the server. Escape or a click outside closes it.
 */
export function FacilityMenu({
  current,
  onPick,
  buildings,
}: {
  current: { id: string; name: string } | null;
  onPick: (f: { id: string; name: string } | null) => void;
  /** The network's building count (policy scope summary), for "All facilities" and the footer. */
  buildings: number | null;
}) {
  const { client } = useSession();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const ctl = new AbortController();
    const t = window.setTimeout(() => {
      facilitySearch(client, { query: { q: q.trim(), limit: SHOWN }, signal: ctl.signal }).then(
        (r) => setRows(r.rows as Row[]),
        () => setRows([]),
      );
    }, q ? WAIT_MS : 0);
    return () => {
      window.clearTimeout(t);
      ctl.abort();
    };
  }, [open, q, client]);

  const needle = q.trim().toLowerCase();
  const showAll = !needle || 'all facilities'.includes(needle);
  const rowStyle = (on: boolean) =>
    'display:flex;align-items:center;gap:10px;min-height:40px;padding:0 10px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;color:#22272E;' + (on ? 'background:rgba(47,92,151,.10);' : '');
  const hits = (rows ?? []).slice(0, showAll ? SHOWN - 1 : SHOWN);
  const none = !!needle && rows !== null && hits.length === 0 && !showAll;
  const foot = buildings === null ? 'Type to search.' : buildings > SHOWN ? 'Type to search. Showing up to ' + SHOWN + ' of ' + buildings + ' facilities.' : buildings + ' facilities in your network.';

  return (
    <span ref={ref} style={sx('position:relative;flex:0 0 auto;')}>
      <a
        href="#"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => { pre(e); setOpen((o) => !o); setQ(''); }}
        style={sx('display:inline-flex;align-items:center;gap:8px;min-height:36px;padding:0 12px;border-radius:9px;border:1px solid rgba(34,39,46,.2);font-size:16px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;')}
      >
        Change facility <span style={sx('color:#54606C;')}>{'▾'}</span>
      </a>
      {open && (
        <span
          role="dialog"
          aria-label="Change facility"
          style={sx('position:absolute;left:0;top:42px;z-index:40;width:340px;max-width:80vw;max-height:300px;overflow-y:auto;display:flex;flex-direction:column;gap:2px;padding:8px;border-radius:12px;background:#FCFAF4;box-shadow:0 12px 36px rgba(34,39,46,.22),0 0 0 1px rgba(34,39,46,.10);animation:riseIn .18s cubic-bezier(.22,.61,.36,1);')}
        >
          <input
            type="text"
            aria-label="Search facilities"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search facilities, city or operator"
            autoFocus
            style={sx('height:40px;box-sizing:border-box;padding:0 12px;border:1px solid rgba(34,39,46,.22);border-radius:9px;background:#fff;font-family:inherit;font-size:16px;color:#22272E;outline:none;margin-bottom:4px;')}
          />
          {showAll && (
            <a href="#" onClick={(e) => { pre(e); onPick(null); setOpen(false); }} style={sx(rowStyle(current === null))}>
              <span style={sx('flex:1 1 auto;min-width:0;overflow-wrap:break-word;')}>All facilities</span>
              <span style={sx('font-size:14px;font-weight:600;color:#54606C;white-space:nowrap;')}>{buildings === null ? 'network' : buildings + ' buildings'}</span>
            </a>
          )}
          {hits.map((r) => (
            <a key={r.id} href="#" onClick={(e) => { pre(e); onPick({ id: r.id, name: r.name }); setOpen(false); }} style={sx(rowStyle(current?.id === r.id))}>
              <span style={sx('flex:1 1 auto;min-width:0;overflow-wrap:break-word;')}>{r.name}</span>
              <span style={sx('font-size:14px;font-weight:600;color:#54606C;white-space:nowrap;')}>{r.residents != null ? r.residents + ' residents' : r.ccn ?? ''}</span>
            </a>
          ))}
          {none && <span style={sx('font-size:16px;color:#22272E;padding:10px 8px;line-height:1.4;')}>{'No facility matches “' + q.trim() + '”. Try a shorter name.'}</span>}
          <span style={sx('font-size:14px;color:#54606C;padding:4px 8px 2px;')}>{foot}</span>
        </span>
      )}
    </span>
  );
}
